import logger from 'helpers/logger';
import system from 'helpers/system';
import styledPackage from 'styled-components/package.json';
import {__PRIVATE__} from 'styled-components';
import hydrationBase from 'theme/hydration.json';
import hydrationDealsuite from 'whiteLabels/dealsuite/theme/hydration.json';

const SEPARATOR = '/*!sc*/';

// A theme changes the css a component generates and so the name that css is stored under,
// but not its componentId or its group. A payload captured under the wrong theme therefore
// only costs coverage, never ordering. The white label comes from the hostname, which is
// known this early, so there is no reason to ship the wrong one anyway.
let hydration = hydrationBase;

const whiteLabel = system.whiteLabel();
if (whiteLabel) {
  if (whiteLabel === 'dealsuite') {
    hydration = hydrationDealsuite;
  }
}

// The app renders into this sheet instead of the one StyleSheetManager builds for itself.
// Hydration has to own the tag it fills: a componentId's group slot is fixed when the
// component is created, and only the owning sheet inserts into those slots. Injecting the
// payload into a tag of its own leaves the app inserting into a second tag that always
// comes last, so an uncovered MUI variant lands after the override written for it.
//
// disableCSSOMInjection would build the same sheet, but as a StyleSheetManager prop it
// reconstructs during render, long after the payload has to be in place. Text injection is
// the deliberate choice either way - see CLAUDE.md.
export const styleSheet = __PRIVATE__.mainSheet.reconstructWithOptions({useCSSOMInjection: false});

// styled-components' own rehydrate() does everything needed: it reads the payload out of
// the document, gives each componentId back the group it was captured under, registers the
// names so nothing is generated twice, and leaves the sheet owning the rules.
//
// What it does badly is get the rules in. It inserts them one at a time into a tag that is
// already in the document, which measured 3.7s for 2224 rules. So the tag is swapped for a
// plain array while it runs, and the rules it collected go into the real tag afterwards
// while that is detached from the document.
function hydrateSheet (sheet, payload) {
  const grouped = sheet.getTag();
  const tag = grouped.tag;
  const rules = [];

  grouped.tag = {
    length: 0,
    insertRule: (index, rule) => {
      rules.splice(index, 0, rule);
      grouped.tag.length = rules.length;
      return true;
    },
    deleteRule: (index) => {
      rules.splice(index, 1);
      grouped.tag.length = rules.length;
    },
    getRule: (index) => {
      return rules[index] ?? '';
    }
  };

  // rehydrate() picks up every style[data-styled] that is not already marked active, and
  // removes it once consumed
  const payloadElement = document.createElement('style');
  payloadElement.setAttribute('data-styled', 'true');
  payloadElement.setAttribute('data-styled-version', payload.version);
  payloadElement.textContent = payload.css;
  document.head.appendChild(payloadElement);

  sheet.rehydrate();

  grouped.tag = tag;

  // Filled while detached, and put back where it was. A style element re-parses all of its
  // content on every child mutation, so filling it in place costs a parse per rule over a
  // sheet that is still growing - 20x the whole operation, measured.
  const element = tag.element;
  const parent = element.parentNode;
  const next = element.nextSibling;

  parent.removeChild(element);
  rules.forEach((rule) => {
    element.appendChild(document.createTextNode(rule));
  });
  parent.insertBefore(element, next);

  // nodes is the live childNodes list, so it lines up one node per rule from here on
  tag.length = rules.length;

  return rules.length;
}

// The routes worth walking: the shared library is most of the stylesheet, so the big pages
// that use it broadly carry the payload. `entities` marks the ones that also have the
// Table / Profile / Browser / Graphs group to click through.
export const hydrationRoutes = [
  {label: 'Dashboard', path: '/'},
  {label: 'Collection', path: '/collections/:collectionId/entities', entities: true, settings: true},
  {label: 'Collections', path: '/collections'},
  {label: 'Database', path: '/database', entities: true},
  {label: 'Deal flow', path: '/dealflow', entities: true},
  {label: 'Settings', path: '/settings', tabs: true}
];

// How many rules the sheet holds right now - the walk watches this to know when a page has
// stopped contributing.
export function hydrationRules () {
  return styleSheet.getTag()?.tag?.length ?? 0;
}

// What still injects after the payload is in - the rules a capture has not covered yet, and
// the number the profile's insertBefore is made of.
//
// The sheet knows which componentId owns which generated name, so each new rule is attributed
// rather than guessed at from the DOM. Frames are counted the way the cost model does, one
// bucket per animation frame that injected anything.
//
//   const watch = __hydrate.watch();   // start
//   ... navigate ...
//   watch.report()                     // counts, worst component first
//   copy(watch.css())                  // the rules themselves
export function watchHydration () {
  const element = styleSheet.getTag()?.tag?.element;
  const rules = [];
  const frames = new Set();
  let frame = 0;

  const tick = () => {
    frame += 1;
    window.requestAnimationFrame(tick);
  };
  window.requestAnimationFrame(tick);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      [...mutation.addedNodes].forEach((node) => {
        rules.push(node.textContent);
        frames.add(frame);
      });
    });
  });

  observer.observe(element, {childList: true});

  const owners = () => {
    const byName = new Map();
    styleSheet.names.forEach((names, id) => {
      names.forEach((name) => {
        byName.set(name, id);
      });
    });
    return byName;
  };

  return {
    stop: () => {
      observer.disconnect();
    },
    // the injected rules themselves, in the order they arrived - pass a componentId from
    // report() to see only that one, which is how a leak is identified: many names for one
    // component means a value is being interpolated into its template rather than passed as
    // a custom property, and no capture will ever cover it
    css: (id) => {
      if (!id) {
        return rules.join('\n');
      }

      const byName = owners();
      return rules.filter((rule) => {
        const name = rule.match(/\.([A-Za-z][A-Za-z0-9_-]*)/)?.[1];
        return name && byName.get(name) === id;
      }).join('\n');
    },
    // Which declarations actually differ between one component's variants. Many names for
    // one component means something is being interpolated into its template; this says what,
    // instead of leaving it to be guessed at from a diff by eye.
    diff: (id) => {
      const byName = owners();
      const values = new Map();

      rules.forEach((rule) => {
        const name = rule.match(/^\.([A-Za-z][A-Za-z0-9_-]*)\{/)?.[1];
        if (!name || byName.get(name) !== id) {
          return;
        }

        (rule.match(/\{([^}]*)\}/)?.[1] ?? '').split(';').forEach((declaration) => {
          const [property, ...rest] = declaration.split(':');
          if (rest.length > 0) {
            const key = property.trim();
            const found = values.get(key) ?? new Map();
            found.set(rest.join(':').trim(), (found.get(rest.join(':').trim()) ?? 0) + 1);
            values.set(key, found);
          }
        });
      });

      return [...values.entries()].filter(([, found]) => {
        return found.size > 1;
      }).sort((a, b) => {
        return b[1].size - a[1].size;
      }).map(([property, found]) => {
        return {property, distinct: found.size, values: [...found.keys()].slice(0, 6)};
      });
    },
    report: () => {
      const byName = owners();
      const components = new Map();
      const unknown = [];

      rules.forEach((rule) => {
        const name = rule.match(/\.([A-Za-z][A-Za-z0-9_-]*)/)?.[1];
        const id = name ? byName.get(name) : null;

        if (id) {
          const entry = components.get(id) ?? {id, rules: 0, names: new Set()};
          entry.rules += 1;
          entry.names.add(name);
          components.set(id, entry);
        } else {
          unknown.push(rule.slice(0, 120));
        }
      });

      return {
        rules: rules.length,
        frames: frames.size,
        sheet: element.childNodes.length,
        generated: rules.filter((rule) => {
          return rule.includes('generated_');
        }).length,
        components: [...components.values()].sort((a, b) => {
          return b.rules - a.rules;
        }).map((entry) => {
          return {id: entry.id, rules: entry.rules, names: [...entry.names].length};
        }),
        unattributed: unknown.length,
        sample: rules.slice(0, 10)
      };
    }
  };
}

// createGlobalStyle rules are deliberately left out of the payload, and the asymmetry with
// a normal component is the reason. A component's componentId comes from its file path, so
// editing its css keeps the same id and the same group - the new variant slots back into
// place - and the variant it replaced goes inert, because its class matches nothing.
//
// A global has neither property. Its componentId is hashed from its own content, so an edit
// mints a new id that lands in a new group at the end, and its rules carry no class, so the
// captured ones keep applying next to the edited ones with nothing able to supersede them.
// Deleting a line from Global.styles.js then changes nothing in the browser.
//
// The payoff for capturing them is nil anyway: 8 rules, 860 bytes, and createGlobalStyle
// already injects its whole block in a single frame at boot.
function stripGlobals (css) {
  const marker = /data-styled\.g\d+\[id="([^"]+)"]/;
  const kept = [];
  let pending = [];

  css.split(SEPARATOR).forEach((segment) => {
    const id = segment.match(marker)?.[1];
    if (id) {
      if (!id.startsWith('sc-global')) {
        kept.push(...pending, segment);
      }
      pending = [];
    } else {
      pending.push(segment);
    }
  });

  return kept.concat(pending).join(SEPARATOR);
}

// The sheet writes the payload itself, in group order, with the names each group registered.
// Every rule the session produced is in it, so a capture taken after browsing is a superset
// of the payload it started from - delete src/theme/hydration.json to start over.
export function captureHydration () {
  const css = stripGlobals(styleSheet.toString());
  const groups = (css.match(/data-styled\.g\d+\[/g) ?? []).length;

  return {
    version: styledPackage.version,
    groups: groups,
    rules: css.split(SEPARATOR).length - 1 - groups,
    css: css
  };
}

// Loading /dev/hydrate directly starts with an empty sheet on purpose, so a capture taken
// after walking holds only what the walk produced - no rules carried over from a payload
// that may still list classes no component emits any more. Only a real page load reaches
// this: routing to the page from inside the app happens long after.
const capturing = window.location.pathname.replace(/\/$/, '').endsWith('/dev/hydrate');

if (capturing) {
  logger.trace('[hydrate] skipped - /dev/hydrate starts fresh');
} else if (hydration?.css?.length > 0) {
  if (hydration.version !== styledPackage.version) {
    logger.error(`[hydrate] payload was captured with styled-components ${hydration.version} ` +
      `but ${styledPackage.version} is installed - recapture src/theme/hydration.json`);
  } else {
    const start = performance.now();
    const count = hydrateSheet(styleSheet, hydration);

    logger.trace(`[hydrate] ${hydration.groups} groups, ${hydration.rules} rules, ` +
      `${Math.round(hydration.css.length / 1024)}kb -> ${count} rules in ` +
      `${(performance.now() - start).toFixed(1)}ms`);
  }
}
